/**
 * The `onward_click` vocabulary (HB-967).
 *
 * A "section" is a destination people think of as a place on a site — News,
 * Editions, an artist's page — not a route. The names themselves are site
 * knowledge, so they stay a plain `string` here and each site supplies its own
 * set through `SectionConfig`; this package only knows how to resolve them.
 */
export type Section = string;

/**
 * What the visitor is navigating *to*, independent of which section holds it.
 *
 * `to_section` says where they landed; `target_type` says what kind of thing
 * they went to see. The pair is what makes the funnel legible: an artist page
 * reached from a News article and an artwork reached from that same article are
 * different depths of intent.
 */
export type TargetType =
	| 'artist'
	| 'artwork'
	| 'product'
	| 'collection'
	| 'exhibition'
	| 'article'
	| 'section-home'
	| 'other';

/** The section a route prefix belongs to. */
export type SectionPrefix = {
	prefix: string;
	section: Section;
};

/**
 * A site's route map, supplied by the site rather than hardcoded here.
 *
 * `sectionPrefixes` maps route prefixes onto sections. `detailTargets` names the
 * target type a single-slug-deep page represents in a given section, so that
 * `/artists/damien-hirst` reads as an `artist` while `/editions/the-currency`
 * reads as an `artwork`. `internalHostnames` are the hosts that count as this
 * site; anything else is a link away and resolves to `other`.
 */
export type SectionConfig = {
	sectionPrefixes: ReadonlyArray<SectionPrefix>;
	detailTargets: Readonly<Record<Section, TargetType>>;
	internalHostnames: ReadonlyArray<string>;
};

/**
 * The `onward_click` payload.
 *
 * Snake_case to match the wire property names of the existing events
 * (`traffic_segment`, `content_type`, `artwork_id`), not the camelCase used
 * inside the app.
 */
export type OnwardClickProperties = {
	from_section: Section;
	to_section: Section;
	target_type: TargetType;
	/** The element the click came from — `nav`, `drop-card`, `artist-link`, … */
	source_element: string;
	/** Present when the click carries an artist through to the destination. */
	artist?: string;
};

/** The section a path resolves to when nothing in the config matches. */
export const UNKNOWN_SECTION: Section = 'other';
